/**
 * Mini mapa da cotação de deslocamento (PoolTrack, 04/10): os pontos (A
 * origem, B destino, C retorno) e o traçado de cada perna. Os marcadores são
 * ARRASTÁVEIS — soltar um marcador avisa o diálogo, que recalcula a(s)
 * perna(s) daquele ponto e pede UMA geocodificação reversa para o texto do
 * endereço. Nada é consultado durante o arraste.
 *
 * É um mapa do GOOGLE (Maps JavaScript API; F-112): os termos do Google Maps
 * Platform não deixam mostrar conteúdo do Geocoding, do Places e do Routes num
 * mapa de outro fornecedor. A chave é a do NAVEGADOR (restrita por referrer a
 * erp.fatorh.ai e só à Maps JavaScript API), que a API entrega em tempo de
 * execução. O script só é pedido quando o diálogo abre com uma chave.
 *
 * Marcadores: com Map ID configurado, `AdvancedMarkerElement` (o recomendado
 * pelo Google); sem Map ID, o `google.maps.Marker` clássico — o avançado exige
 * um Map ID. O traçado é `google.maps.Polyline`: ida cheia, volta tracejada.
 *
 * Limitado ao Brasil (operador, 04/10): o mapa não sai do retângulo do Brasil
 * (com margem) nem afasta além do zoom em que o país inteiro aparece. O
 * marcador ainda pode ser solto num vizinho perto da fronteira — quem decide
 * é o diálogo (`onMove` devolve false), e aí ele volta para onde estava.
 *
 * Sem mapa (script bloqueado, fora do prazo, chave recusada): `onUnavailable`,
 * e o diálogo segue sem ele.
 */
import { useEffect, useRef, useState } from 'react';
import { BRAZIL_BOUNDS, type QuoteLegKey, type QuotePointKey } from '@erp/shared';
import { loadGoogleMaps, onGoogleMapsAuthFailure, type GoogleMapsLibs } from './google-maps-loader';

export const QUOTE_POINT_COLORS: Record<QuotePointKey, string> = {
  origin: '#2563eb',
  destination: '#dc2626',
  returnTo: '#7c3aed',
};

export const QUOTE_POINT_LETTERS: Record<QuotePointKey, string> = {
  origin: 'A',
  destination: 'B',
  returnTo: 'C',
};

/** ida: linha cheia; volta: tracejada (traço como símbolo repetido — é assim no Google) */
function legStyle(key: QuoteLegKey): google.maps.PolylineOptions {
  if (key === 'ida') {
    return { strokeColor: '#2563eb', strokeWeight: 5, strokeOpacity: 0.85 };
  }
  return {
    strokeColor: '#ea580c',
    strokeOpacity: 0,
    strokeWeight: 4,
    icons: [
      {
        icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.85, strokeWeight: 4, scale: 3 },
        offset: '0',
        repeat: '16px',
      },
    ],
  };
}

/** centro de Belo Horizonte — só até o primeiro ponto aparecer */
const START: google.maps.LatLngLiteral = { lat: -19.9191, lng: -43.9386 };

/** o retângulo do Brasil (com Trindade e Martim Vaz) e 2° de margem: dá para ver a fronteira */
const BOUNDS_PAD_DEG = 2;
const MAX_BOUNDS: google.maps.LatLngBoundsLiteral = {
  south: BRAZIL_BOUNDS.south - BOUNDS_PAD_DEG,
  west: BRAZIL_BOUNDS.west - BOUNDS_PAD_DEG,
  north: BRAZIL_BOUNDS.north + BOUNDS_PAD_DEG,
  east: BRAZIL_BOUNDS.east + BOUNDS_PAD_DEG,
};
/** no zoom 3 o país inteiro cabe no mapa do diálogo (~520 × 320 px); menos que isso é o mundo */
const MIN_ZOOM = 3;
/** o enquadramento depois do "Calcular" não chega mais perto que isto */
const MAX_FIT_ZOOM = 15;

export interface QuoteMapPoint {
  key: QuotePointKey;
  lat: number;
  lng: number;
}

export interface QuoteMapLeg {
  key: QuoteLegKey;
  /** traçado do provedor; vazio = reta entre as pontas */
  path: Array<[number, number]>;
  from: { lat: number; lng: number };
  to: { lat: number; lng: number };
}

export type QuoteMapUnavailableReason = 'load' | 'auth';

/** o círculo com a letra, para o marcador avançado (o clássico usa símbolo + rótulo) */
function pinContent(key: QuotePointKey, draggable: boolean): HTMLElement {
  const el = document.createElement('div');
  el.textContent = QUOTE_POINT_LETTERS[key];
  el.style.cssText = [
    'width:28px',
    'height:28px',
    'border-radius:9999px',
    `background:${QUOTE_POINT_COLORS[key]}`,
    'color:#fff',
    'border:2px solid #fff',
    'box-shadow:0 1px 4px rgba(0,0,0,.45)',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'font:600 13px/1 Inter,system-ui,sans-serif',
    // o avançado ancora o conteúdo pela base: meia altura para baixo = centro no ponto
    'transform:translateY(50%)',
    `cursor:${draggable ? 'grab' : 'default'}`,
  ].join(';');
  return el;
}

const latLngOf = (
  p: google.maps.LatLng | google.maps.LatLngLiteral | google.maps.LatLngAltitudeLiteral
): { lat: number; lng: number } =>
  typeof p.lat === 'function'
    ? { lat: (p as google.maps.LatLng).lat(), lng: (p as google.maps.LatLng).lng() }
    : { lat: p.lat as number, lng: p.lng as number };

/**
 * Um marcador arrastável. `onDrop` decide: false = recusado, e o marcador volta
 * para `p`. Devolve a função que o tira do mapa.
 */
function addMarker(
  libs: GoogleMapsLibs,
  map: google.maps.Map,
  p: QuoteMapPoint,
  opts: { draggable: boolean; advanced: boolean },
  onDrop: (lat: number, lng: number) => boolean
): () => void {
  const position = { lat: p.lat, lng: p.lng };
  const title = QUOTE_POINT_LETTERS[p.key];
  const round = (v: number) => Number(v.toFixed(6));
  if (opts.advanced) {
    const marker = new libs.marker.AdvancedMarkerElement({
      map,
      position,
      title,
      gmpDraggable: opts.draggable,
      content: pinContent(p.key, opts.draggable),
    });
    const listener = marker.addListener('dragend', () => {
      if (!marker.position) return;
      const at = latLngOf(marker.position);
      if (!onDrop(round(at.lat), round(at.lng))) marker.position = position;
    });
    return () => {
      listener.remove();
      marker.map = null;
    };
  }
  const marker = new libs.marker.Marker({
    map,
    position,
    title,
    draggable: opts.draggable,
    label: { text: title, color: '#ffffff', fontSize: '13px', fontWeight: '600' },
    icon: {
      path: libs.core.SymbolPath.CIRCLE,
      scale: 13,
      fillColor: QUOTE_POINT_COLORS[p.key],
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 2,
    },
  });
  const listener = marker.addListener('dragend', (e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;
    if (!onDrop(round(e.latLng.lat()), round(e.latLng.lng()))) marker.setPosition(position);
  });
  return () => {
    listener.remove();
    marker.setMap(null);
  };
}

export function QuoteMap({
  browserKey,
  mapId,
  points,
  legs,
  onMove,
  onUnavailable,
  fitKey,
  draggable = true,
  className,
}: {
  /** a chave do NAVEGADOR (vem da API); o componente só existe com ela */
  browserKey: string;
  /** com Map ID, marcadores avançados; sem, o clássico */
  mapId: string | null;
  points: QuoteMapPoint[];
  legs: QuoteMapLeg[];
  /** fim do arraste de um marcador; `false` = recusado, o marcador volta para onde estava */
  onMove: (key: QuotePointKey, lat: number, lng: number) => boolean;
  /** o mapa não carregou ou a chave foi recusada: o diálogo segue sem ele */
  onUnavailable: (reason: QuoteMapUnavailableReason) => void;
  /** muda quando o enquadramento deve ser refeito (depois de um "Calcular") */
  fitKey: number;
  draggable?: boolean;
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const libsRef = useRef<GoogleMapsLibs | null>(null);
  const [ready, setReady] = useState(false);
  // os últimos callbacks, sem recriar marcadores a cada render
  const onMoveRef = useRef(onMove);
  onMoveRef.current = onMove;
  const onUnavailableRef = useRef(onUnavailable);
  onUnavailableRef.current = onUnavailable;

  useEffect(() => {
    let cancelled = false;
    const unsubscribe = onGoogleMapsAuthFailure(() => onUnavailableRef.current('auth'));
    loadGoogleMaps(browserKey).then(
      (libs) => {
        const el = containerRef.current;
        if (cancelled || !el) return;
        libsRef.current = libs;
        mapRef.current = new libs.maps.Map(el, {
          center: START,
          zoom: 11,
          minZoom: MIN_ZOOM,
          restriction: { latLngBounds: MAX_BOUNDS, strictBounds: true },
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          clickableIcons: false,
          ...(mapId ? { mapId } : {}),
        });
        setReady(true);
      },
      () => {
        if (!cancelled) onUnavailableRef.current('load');
      }
    );
    return () => {
      cancelled = true;
      unsubscribe();
      mapRef.current = null;
      libsRef.current = null;
      setReady(false);
    };
  }, [browserKey, mapId]);

  // pontos e pernas: redesenha tudo (são no máximo 3 + 2)
  useEffect(() => {
    const map = mapRef.current;
    const libs = libsRef.current;
    if (!ready || !map || !libs) return;
    const removers: Array<() => void> = [];
    for (const leg of legs) {
      const path =
        leg.path.length > 1 ? leg.path.map(([lat, lng]) => ({ lat, lng })) : [leg.from, leg.to];
      const line = new libs.maps.Polyline({ map, path, clickable: false, ...legStyle(leg.key) });
      removers.push(() => line.setMap(null));
    }
    for (const p of points) {
      removers.push(
        addMarker(libs, map, p, { draggable, advanced: mapId !== null }, (lat, lng) =>
          onMoveRef.current(p.key, lat, lng)
        )
      );
    }
    return () => {
      for (const remove of removers) remove();
    };
  }, [ready, points, legs, draggable, mapId]);

  // enquadra quando o diálogo pede (depois de calcular) e quando o mapa fica
  // pronto com pontos já calculados — não a cada arraste
  useEffect(() => {
    const map = mapRef.current;
    const libs = libsRef.current;
    if (!ready || !map || !libs || points.length === 0) return;
    const bounds = new libs.core.LatLngBounds();
    for (const p of points) bounds.extend({ lat: p.lat, lng: p.lng });
    for (const leg of legs) for (const [lat, lng] of leg.path) bounds.extend({ lat, lng });
    map.fitBounds(bounds, 32);
    const clamp = libs.core.event.addListenerOnce(map, 'idle', () => {
      if ((map.getZoom() ?? 0) > MAX_FIT_ZOOM) map.setZoom(MAX_FIT_ZOOM);
    });
    return () => clamp.remove();
    // só o fitKey (e o mapa ficar pronto) decide: os pontos mudam no arraste,
    // e o mapa não deve pular
  }, [fitKey, ready]);

  return (
    <div
      ref={containerRef}
      className={className}
      data-testid="quote-map"
      data-marker={mapId ? 'advanced' : 'classic'}
    />
  );
}
